import { useEffect } from 'react';

const DEFAULT_TITLE =
  'SMG Media Group — Website Development, Paid Ads, SEO & Analytics Agency (Carlsbad, CA)';
const DEFAULT_DESC =
  'SMG Media Group is a full-service marketing agency in Carlsbad, California specializing in website design and development, paid ads (Google Ads, Meta Ads, TikTok Ads), SEO, marketing analytics, brand identity, and social media marketing for lifestyle, D2C, and challenger brands. Studios in Carlsbad, CA and Nashville, TN.';
const SITE_URL = 'https://smgmediagroup.com';
const DEFAULT_OG_IMAGE =
  'https://customer-assets.emergentagent.com/job_cinema-hub-693/artifacts/jhtdaos4_SMG%20ALL%20LOGO%20PNG.png';

/**
 * Injects/updates <title>, meta description, canonical, Open Graph, Twitter Card,
 * and JSON-LD structured data. Also wires up GA4 + GSC when env vars are present.
 */
function upsertMeta({ name, property, content }) {
  if (!content) return;
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    if (name) el.setAttribute('name', name);
    if (property) el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink({ rel, href }) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id, payload) {
  let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-jsonld', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
}

function ensureGa4() {
  const id = process.env.REACT_APP_GA4_ID;
  if (!id || document.getElementById('ga4-loader')) return;
  const s1 = document.createElement('script');
  s1.id = 'ga4-loader';
  s1.async = true;
  s1.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s1);
  const s2 = document.createElement('script');
  s2.id = 'ga4-init';
  s2.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);} gtag('js',new Date()); gtag('config','${id}');`;
  document.head.appendChild(s2);
}

function ensureGsc() {
  const token = process.env.REACT_APP_GSC_VERIFICATION;
  if (!token) return;
  let el = document.head.querySelector('meta[name="google-site-verification"]');
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', 'google-site-verification');
    document.head.appendChild(el);
  }
  el.setAttribute('content', token);
}

function ensureClarity() {
  const id = process.env.REACT_APP_CLARITY_ID;
  if (!id || document.getElementById('clarity-loader')) return;
  const s = document.createElement('script');
  s.id = 'clarity-loader';
  s.type = 'text/javascript';
  s.text = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${id}");`;
  document.head.appendChild(s);
}

export default function SeoHead({
  title,
  description,
  path = '/',
  image,
  type = 'website',
  jsonLd,
}) {
  useEffect(() => {
    const finalTitle = title || DEFAULT_TITLE;
    const finalDesc = description || DEFAULT_DESC;
    const finalUrl = `${SITE_URL}${path}`;
    const finalImage = image || DEFAULT_OG_IMAGE;

    document.title = finalTitle;
    upsertMeta({ name: 'description', content: finalDesc });
    upsertLink({ rel: 'canonical', href: finalUrl });

    // Open Graph
    upsertMeta({ property: 'og:title', content: finalTitle });
    upsertMeta({ property: 'og:description', content: finalDesc });
    upsertMeta({ property: 'og:url', content: finalUrl });
    upsertMeta({ property: 'og:type', content: type });
    upsertMeta({ property: 'og:image', content: finalImage });
    upsertMeta({ property: 'og:site_name', content: 'SMG Media Group' });

    // Twitter Card
    upsertMeta({ name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta({ name: 'twitter:title', content: finalTitle });
    upsertMeta({ name: 'twitter:description', content: finalDesc });
    upsertMeta({ name: 'twitter:image', content: finalImage });

    // NOTE: Organization, LocalBusiness (Carlsbad + Nashville), and WebSite
    // schemas are baked into /public/index.html so crawlers see them without
    // JS execution. We only inject per-route custom schemas here.

    // Per-route custom JSON-LD (e.g., case study CreativeWork, Article, BreadcrumbList, FAQPage)
    // Supports either a single object or an array for multiple schemas per page.
    if (jsonLd) {
      const list = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      list.forEach((entry, i) => {
        upsertJsonLd(`route-${path}-${i}`, entry);
      });
    }

    ensureGa4();
    ensureGsc();
    ensureClarity();
  }, [title, description, path, image, type, jsonLd]);

  return null;
}
